import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PageHeader } from "@/components/PageHeader";
import { DigitBoxes, RevealButton, SecretInput, weakPin } from "@/components/login/Secret";
import { rememberDevice } from "@/lib/trusted";
import { fmtDateTime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { isIOS, isStandalone } from "@/lib/pwa";
import { disablePush, enablePush, pushEnabled, pushSupported } from "@/lib/push";

const PasswordForm = () => {
  const [v, setV] = useState({ current_password: "", new_password: "", confirm: "" });
  const [shown, setShown] = useState(false);
  const [busy, setBusy] = useState(false);
  const mismatch = v.confirm && v.confirm !== v.new_password;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/auth/password", { current_password: v.current_password, new_password: v.new_password }); toast.success("Password changed"); setV({ current_password: "", new_password: "", confirm: "" }); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3 rounded-lg border border-slate-200 bg-white p-6" data-testid="password-form">
      <h2 className="text-base font-semibold text-slate-900">Change password</h2>
      {[["current_password", "Current password", "current-password"], ["new_password", "New password (at least 8 characters)", "new-password"], ["confirm", "Confirm new password", "new-password"]].map(([k, l, ac]) => (
        <div key={k} className="max-w-sm space-y-1.5"><Label className="text-xs">{l}</Label><SecretInput value={v[k]} onChange={(x) => setV({ ...v, [k]: x })} shown={shown} onToggle={() => setShown(!shown)} autoComplete={ac} testid={`profile-${k}-input`} /></div>
      ))}
      {mismatch && <p className="text-xs text-red-600" data-testid="profile-password-mismatch">Passwords don't match</p>}
      <Button type="submit" disabled={busy || mismatch || v.new_password.length < 8 || !v.current_password} className="bg-teal-600 hover:bg-teal-700" data-testid="profile-password-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Change password"}</Button>
    </form>
  );
};

const PinForm = ({ onChange }) => {
  const { user, refreshUser } = useAuth();
  const has = !!user.pin_set;
  const [cur, setCur] = useState("");
  const [curPin, setCurPin] = useState("");
  const [usePin, setUsePin] = useState(false);
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [shown, setShown] = useState(false);
  const [busy, setBusy] = useState("");
  const rule = a.length === 4 ? weakPin(a) : null;
  const proof = usePin ? { current_pin: curPin } : { current_password: cur };
  const proved = usePin ? curPin.length === 4 : !!cur;
  const reset = () => { setCur(""); setCurPin(""); setA(""); setB(""); };
  const run = async (key, path, body, msg) => {
    setBusy(key);
    try { const r = await api.post(path, body); rememberDevice(r.data.device); await refreshUser(); reset(); toast.success(msg); onChange?.(); } catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };
  return (
    <form onSubmit={(e) => { e.preventDefault(); run("save", "/auth/pin", { ...proof, new_pin: a, scope: localStorage.getItem("pnl_login_slug") || "" }, has ? "PIN changed · other devices signed out of PIN" : "PIN set · this device can now sign in with it"); }} className="space-y-3 rounded-lg border border-slate-200 bg-white p-6" data-testid="pin-form">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-slate-900">{has ? "Change sign-in PIN" : "Set a sign-in PIN"}</h2>
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${has ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid="pin-status">{has ? (user.pin_locked ? "PIN locked" : "PIN set") : "No PIN"}</span>
      </div>
      <p className="text-sm text-slate-500">After one password sign-in on a device, sign in there with just your 4-digit PIN. Avoid repeated digits, sequences and years. Changing or removing the PIN forgets all trusted devices.</p>
      <div className="max-w-sm space-y-1.5">
        <div className="flex items-center justify-between"><Label className="text-xs">{usePin ? "Current PIN" : "Current password"}</Label>
          {has && <button type="button" className="min-h-[44px] text-xs font-medium text-teal-700 hover:underline" onClick={() => setUsePin(!usePin)} data-testid="pin-proof-toggle">{usePin ? "Use password instead" : "Use current PIN instead"}</button>}</div>
        {usePin ? <DigitBoxes value={curPin} onChange={setCurPin} shown={shown} label="Current PIN" testid="pin-current" />
          : <SecretInput value={cur} onChange={setCur} shown={shown} onToggle={() => setShown(!shown)} autoComplete="current-password" testid="pin-current-password" />}
      </div>
      <div className="max-w-sm space-y-1.5"><div className="flex items-center justify-between"><Label className="text-xs">New PIN</Label><RevealButton shown={shown} onToggle={() => setShown(!shown)} label="PIN" testid="pin-toggle" /></div><DigitBoxes value={a} onChange={setA} shown={shown} label="New PIN" testid="pin-new" /></div>
      <div className="max-w-sm space-y-1.5"><Label className="text-xs">Confirm new PIN</Label><DigitBoxes value={b} onChange={setB} shown={shown} label="Confirm PIN" testid="pin-confirm" /></div>
      {(rule || (b.length === 4 && a !== b)) && <p className="text-xs text-red-600" data-testid="pin-rule">{rule || "The PINs don't match"}</p>}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={!!busy || !proved || a.length !== 4 || a !== b || !!rule} className="bg-teal-600 hover:bg-teal-700" data-testid="pin-submit">{busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : has ? "Change PIN" : "Set PIN"}</Button>
        {has && <Button type="button" variant="outline" disabled={!!busy || !proved} onClick={() => window.confirm("Remove your sign-in PIN?") && run("remove", "/auth/pin/remove", proof, "PIN removed")} className="text-red-600" data-testid="pin-remove">{busy === "remove" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Remove PIN"}</Button>}
      </div>
    </form>
  );
};

const TrustedDevices = () => {
  const [list, setList] = useState(null);
  const load = () => api.get("/auth/devices").then((r) => setList(r.data.devices)).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  const remove = async (d) => { try { await api.delete(`/auth/devices/${d.id}`); load(); toast.success("Device removed"); } catch (err) { toast.error(apiError(err)); } };
  return (
    <div id="trusted-devices" className="scroll-mt-24 space-y-3 rounded-lg border border-slate-200 bg-white p-6" data-testid="trusted-devices">
      <h2 className="text-base font-semibold text-slate-900">Trusted devices</h2>
      <p className="text-sm text-slate-500">Devices that can sign in with your PIN. They're forgotten after 90 days without use.</p>
      {list === null ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : list.length === 0 ? <p className="text-sm text-slate-400" data-testid="trusted-devices-empty">No trusted devices</p> : (
        <ul className="divide-y divide-slate-100">
          {list.map((d) => (
            <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5" data-testid={`trusted-device-${d.id}`}>
              <div className="min-w-0"><div className="text-sm font-medium text-slate-800">{d.label}</div><div className="text-xs text-slate-500">{d.scope ? `/login/${d.scope}` : "/login"} · last used {fmtDateTime(d.last_used_at)}</div></div>
              <Button size="sm" variant="outline" className="h-11 text-red-600 sm:h-9" onClick={() => remove(d)} data-testid={`trusted-device-remove-${d.id}`}>Remove</Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const PushToggle = () => {
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { pushEnabled().then(setOn); }, []);
  const toggle = async (v) => {
    setBusy(true);
    try { if (v) await enablePush(); else await disablePush(); setOn(v); toast.success(v ? "Browser pop-ups are on" : "Browser pop-ups are off"); }
    catch (e) { toast.error(e.message || apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid="push-settings">
      <h2 className="text-base font-semibold text-slate-900">Browser pop-ups</h2>
      <p className="mt-1 text-sm text-slate-500">Show task updates as pop-up notifications on this device, even when the tab is in the background.</p>
      {isIOS() && !isStandalone() ? <p className="mt-3 text-sm text-amber-700" data-testid="push-ios-hint-profile">On iPhone or iPad, add this app to your Home Screen (Share → Add to Home Screen) and open it from there to turn on notifications. Requires iOS 16.4 or later.</p> : pushSupported() ? (
        <label className="mt-3 flex items-center gap-2 text-sm"><Switch checked={on} disabled={busy} onCheckedChange={toggle} data-testid="push-toggle" /> {on ? "On for this browser" : "Off for this browser"}</label>
      ) : <p className="mt-3 text-sm text-amber-700" data-testid="push-unsupported">This browser doesn't support pop-up notifications.</p>}
    </div>
  );
};

export default function ProfilePage() {
  const [tick, setTick] = useState(0);
  const { user } = useAuth();
  return (
    <div className="max-w-3xl space-y-6" data-testid="profile-page">
      <PageHeader eyebrow="Account" title="Profile" subtitle={`${user.name} · ${user.email} · ${user.role_label}`} />
      <PushToggle />
      <PasswordForm />
      <PinForm onChange={() => setTick((t) => t + 1)} />
      <TrustedDevices key={tick} />
    </div>
  );
}
